import { useSyncExternalStore } from "react";
import { en } from "./en";
import { ar } from "./ar";

/**
 * Two languages, one key list.
 *
 * REGISTER — decided deliberately, because getting it wrong wastes every string:
 *
 *  - Instructional and connective copy is KUWAITI, not MSA. Buttons, empty states
 *    and helper text say what a Kuwaiti would say: ما في, باقي, تبي, شنو. The
 *    backlog asks for "Kuwaiti phrasing, not translated English", and the
 *    kuwaiti-dialect skill's first hard rule is never pure MSA.
 *
 *  - Financial and contractual NOUNS stay standard: رصيد, كشف حساب, عقد, فاتورة.
 *    There is no colloquial alternative a Kuwaiti finance team would recognise, and
 *    inventing one would read as a translation error rather than as local voice.
 *    The dialect belongs in the sentences, not in the accounting terms.
 *
 *  - Everything is GENDER-NEUTRAL. The skill's gender-matching rule assumes one
 *    addressee in a chat; an interface is read by a whole team, so second-person
 *    forms that must agree (تبي/تبين) are avoided in favour of impersonal or
 *    plural phrasing. This is the one skill rule an interface cannot honour as
 *    written, and it is a deliberate exception rather than an oversight.
 *
 * `ar.ts` is checked against the skill's banned-MSA list by src/i18n.test.ts, so a
 * lapse into كيف / ماذا / الآن fails the build rather than reaching a client.
 */

export type Language = "en" | "ar";
export type TranslationKey = keyof typeof en;

const DICTIONARIES: Record<Language, Record<string, string>> = { en, ar };

const KEY = "zain-credits-language";

/**
 * The reader's language: their explicit choice if they have made one, otherwise
 * what their BROWSER asks for.
 *
 * It used to default to English full stop. Two readers are badly served by that.
 * A Zain employee opening the dashboard for the first time on an Arabic browser
 * had to find the toggle before the product spoke to them — and the public share
 * page has no session at all, so a stakeholder opening a link had no stored
 * preference to read and always got English.
 *
 * An explicit choice always wins: once somebody switches, the switch sticks, even
 * if it disagrees with their browser.
 */
export function readLanguage(): Language {
  try {
    const chosen = window.localStorage.getItem(KEY);
    if (chosen === "ar" || chosen === "en") return chosen;
  } catch {
    // Private mode, or storage disabled. Fall through to the browser.
  }
  try {
    // `ar`, `ar-KW`, `ar-EG` — any Arabic locale. Anything else is English,
    // because English is the only other language this product speaks.
    const preferred = navigator.languages?.[0] || navigator.language || "";
    return preferred.toLowerCase().startsWith("ar") ? "ar" : "en";
  } catch {
    return "en";
  }
}

export function applyLanguage(language: Language) {
  const root = document.documentElement;
  root.setAttribute("lang", language);
  // The whole layout mirrors off this one attribute — the stylesheet is written in
  // logical properties, so nothing else has to know.
  root.setAttribute("dir", language === "ar" ? "rtl" : "ltr");
  try {
    window.localStorage.setItem(KEY, language);
  } catch {
    // Not being able to remember the choice must not stop it applying now.
  }
}

/** Before React mounts, so the first paint is already in the right direction. */
export function initLanguage() {
  applyLanguage(readLanguage());
}

/**
 * Falls back to English rather than printing the key. A missing translation should
 * read as an untranslated label, which is merely incomplete — `requests.empty.title`
 * on screen is broken.
 */
export function translate(language: Language, key: string): string {
  // An unknown key returns ITSELF, which is how the Prismic-only screens keep their
  // English labels without needing a dictionary entry each.
  return DICTIONARIES[language][key] ?? (en as Record<string, string>)[key] ?? key;
}

/**
 * React binding.
 *
 * A module-level store plus useSyncExternalStore rather than a context provider:
 * every component in the tree needs the language, and threading a provider through
 * would touch more files than the translations themselves. Changing language
 * re-renders everything, which is exactly right — everything changed.
 */
let current: Language = "en";
const listeners = new Set<() => void>();

export function setLanguage(language: Language) {
  current = language;
  applyLanguage(language);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function snapshot() {
  return current;
}

/** Call once before render, alongside initLanguage. */
export function primeLanguage() {
  current = readLanguage();
  applyLanguage(current);
}

/** Accepts a key for autocomplete, or a plain English literal for the screens that
 *  are deliberately not translated. */
export type Translator = (key: TranslationKey | (string & {}), values?: Record<string, string | number>) => string;

/**
 * The whole API a component needs: `t("nav.overview")`, or with values,
 * `t("overview.readyHeading", { n: 108 })`.
 */
/**
 * "1 months in".
 *
 * Translating `{n} service{s}` into `{n} services` lost the singular the inline
 * JSX had been handling, and the renewal band shipped "1 months in" — a small
 * wrongness that makes a reader distrust the number next to it. Rather than an
 * ICU plural runtime, a key may declare a `_one` twin and it wins when n is 1.
 * Both dictionaries must carry it, which src/i18n.test.ts already enforces by
 * requiring identical key lists.
 *
 * Only n === 1 is special-cased. Arabic's dual and 3-10 forms are real, but every
 * plural string here reads correctly with a Western numeral and the singular noun
 * ("باقي 10 شهر") — which is how a Kuwaiti writes it. Building the full ladder for
 * forms nothing needs would be machinery without a reader.
 */
function pluralKey(key: string, values?: Record<string, string | number>): string {
  return values && Number(values.n) === 1 ? `${key}_one` : key;
}

export function useT(): Translator {
  const language = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (key, values) => {
    const wanted = pluralKey(String(key), values);
    // An unknown key returns itself, so a missing `_one` must fall back to the
    // plural rather than printing "renewal.elapsed_one" on the screen.
    let text = wanted === key ? translate(language, String(key)) : translate(language, wanted);
    if (text === wanted && wanted !== key) text = translate(language, String(key));
    if (values) {
      for (const [name, value] of Object.entries(values)) {
        text = text.replaceAll(`{${name}}`, String(value));
      }
    }
    return text;
  };
}

/** For the switch itself, which needs to know what is currently selected. */
export function useLanguage(): Language {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
